import {
  PulseAccountSocketPayload,
  PulseAccountsData,
  PulseBankAccount,
  PulseDocSummary,
  PulseDeadlineSubmittedSocketPayload,
  PulseIdentificationSocketPayload,
  PulseOcrSocketPayload,
  PulseReconciliationFailedSocketPayload,
  PulseReconciliationSocketPayload,
  PulseTransactionConsentResult,
} from "./types";
import {
  ANALYSIS_FAILURE_STATUS,
  ANALYSIS_SUCCESS_STATUS,
  NOT_SUBMITTED_STATUS,
  OCR_COMPLETED_STATUS,
  OCR_PENDING_STATUS,
  STATEMENT_PROCESSING_FAILED_ERROR,
  SUBMITTED_STATUS,
  SUBMITTING_STATUS,
  isDocumentAwaitingSubmission,
  isDocumentPasswordRequired,
  pipelineStatusFromAnalysisStatus,
  pipelineStatusFromAccount,
  uniqueDocuments,
} from "./utils";

export interface BankStatementsState extends PulseAccountsData {
  loading: boolean;
  loaded: boolean;
  loadError: string | null;
  submissionError: string | null;
  submittedInSession: boolean;
  busyScopes: string[];
}

export const initialState: BankStatementsState = {
  companyId: 0,
  companyName: "",
  pendingIdentification: [],
  accounts: [],
  loading: true,
  loaded: false,
  loadError: null,
  submissionError: null,
  submittedInSession: false,
  busyScopes: [],
};

export type Action =
  | { type: "LOAD_START" }
  | { type: "LOAD_SUCCESS"; payload: PulseAccountsData }
  | { type: "LOAD_ERROR"; payload: string }
  | { type: "SET_SCOPE_BUSY"; scope: string; busy: boolean }
  | {
      type: "STAGE_DOCUMENT";
      doc: PulseDocSummary;
      accountId?: number;
    }
  | {
      type: "UPDATE_DOCUMENT";
      doc: PulseDocSummary;
    }
  | { type: "REMOVE_DOCUMENTS"; docIds: number[] }
  | {
      type: "DELETE_ACCOUNT";
      accountId: number;
      deletedDocIds: number[];
    }
  | { type: "ANALYSIS_SUBMIT_START"; docIds: number[] }
  | { type: "ANALYSIS_SUBMITTED"; docIds: number[] }
  | { type: "ANALYSIS_SUBMIT_ERROR"; message: string; docIds: number[] }
  | { type: "IDENTIFICATION_STARTED"; docIds: number[] }
  | { type: "IDENTIFICATION_RESULT"; payload: PulseIdentificationSocketPayload }
  | { type: "OCR_COMPLETE"; payload: PulseOcrSocketPayload }
  | {
      type: "ACCOUNT_PROCESSING";
      payload: PulseAccountSocketPayload;
      status: "identifying" | "processing" | "failed";
    }
  | {
      type: "RECONCILIATION_COMPLETE";
      payload: PulseReconciliationSocketPayload;
      updatedAt: string;
    }
  | {
      type: "RECONCILIATION_FAILED";
      payload: PulseReconciliationFailedSocketPayload;
      updatedAt: string;
    }
  | {
      type: "DEADLINE_SUBMITTED";
      payload: PulseDeadlineSubmittedSocketPayload;
    }
  | {
      type: "TRANSACTION_CONSENT_UPDATED";
      payload: PulseTransactionConsentResult;
    };

const findDocumentById = (state: BankStatementsState, docId: number) =>
  state.pendingIdentification.find((doc) => doc.id === docId) ||
  state.accounts
    .flatMap((account) => account.documents)
    .find((doc) => doc.id === docId);

// The single choke point for every server document, so client-only fields that
// must outlive a payload belong here. `uploadedForRange` is synthesised on
// upload and would otherwise be dropped on the next refresh whenever the
// backend does not echo it back.
const preserveUiDocumentFields = (
  state: BankStatementsState,
  doc: PulseDocSummary
) => {
  const existing = findDocumentById(state, doc.id);
  return {
    ...doc,
    passwordVerifiedAt: doc.passwordVerifiedAt ?? existing?.passwordVerifiedAt,
    uploadedForRange: doc.uploadedForRange ?? existing?.uploadedForRange,
  };
};

const isLocallyStagedDocument = (doc: PulseDocSummary) =>
  isDocumentAwaitingSubmission(doc) || isDocumentPasswordRequired(doc);

const normalizeAccounts = (
  accounts: PulseBankAccount[],
  currentState: BankStatementsState
) =>
  accounts.map((account) => {
    const incomingIds = new Set((account.documents || []).map((doc) => doc.id));
    const existingAccount = currentState.accounts.find(
      (candidate) => candidate.id === account.id
    );
    const stagedOnly = (existingAccount?.documents || []).filter(
      (doc) => !incomingIds.has(doc.id) && isLocallyStagedDocument(doc)
    );
    return {
      ...account,
      documents: uniqueDocuments([
        ...stagedOnly,
        ...(account.documents || []),
      ]).map((doc) => preserveUiDocumentFields(currentState, doc)),
      pipelineStatus: pipelineStatusFromAccount(account),
    };
  });

const removeDocFromAccounts = (accounts: PulseBankAccount[], docId: number) =>
  accounts.map((account) => ({
    ...account,
    documents: account.documents.filter((doc) => doc.id !== docId),
  }));

const findAccountIndex = (
  accounts: PulseBankAccount[],
  incoming: PulseBankAccount
) => {
  const exactIdIndex = accounts.findIndex(
    (account) => account.id === incoming.id
  );
  if (exactIdIndex !== -1) return exactIdIndex;
  const normalizedNumber = incoming.accountNumber?.replace(/\s/g, "");
  return accounts.findIndex(
    (account) =>
      !!normalizedNumber &&
      account.accountNumber?.replace(/\s/g, "") === normalizedNumber
  );
};

const updateDocumentsById = (
  documents: PulseDocSummary[],
  docIds: Set<number>,
  updater: (doc: PulseDocSummary) => PulseDocSummary
) =>
  documents.map((doc) =>
    docIds.has(doc.id) ? updater(doc) : doc
  );

export const accountMatchesPayload = (
  account: PulseBankAccount,
  payload: PulseAccountSocketPayload
) =>
  (payload.pulseBankAccountId != null &&
    account.id === payload.pulseBankAccountId) ||
  (!!payload.statementJobId && account.statementJobId === payload.statementJobId);

const updateAccountByPayload = (
  accounts: PulseBankAccount[],
  payload: PulseAccountSocketPayload,
  updater: (account: PulseBankAccount) => PulseBankAccount
) =>
  accounts.map((account) =>
    accountMatchesPayload(account, payload) ? updater(account) : account
  );

const applyIdentificationResult = (
  state: BankStatementsState,
  action: Extract<Action, { type: "IDENTIFICATION_RESULT" }>
): BankStatementsState => {
  const payloadResolvedAccounts = action.payload.resolvedAccounts || [];
  const failedIds = new Set(
    (action.payload.failed || []).map((doc) => doc.id)
  );
  const resolvedIds = new Set(
    payloadResolvedAccounts.flatMap((account) =>
      (account.documents || []).map((doc) => doc.id)
    )
  );
  let accounts = state.accounts;

  [...resolvedIds, ...failedIds].forEach((docId) => {
    accounts = removeDocFromAccounts(accounts, docId);
  });

  payloadResolvedAccounts.forEach((incoming) => {
    const normalizedIncoming = {
      ...incoming,
      statementJobId:
        incoming.statementJobId || action.payload.statementJobId,
      documents: uniqueDocuments(incoming.documents || []).map(
        (incomingDocument) => ({
          ...preserveUiDocumentFields(state, incomingDocument),
          ocrProcessingStatus:
            incomingDocument.ocrProcessingStatus || OCR_PENDING_STATUS,
        })
      ),
      pipelineStatus: pipelineStatusFromAccount(incoming),
    };
    const existingIndex = findAccountIndex(accounts, normalizedIncoming);
    if (existingIndex === -1) {
      accounts = [...accounts, normalizedIncoming];
    } else {
      accounts = accounts.map((account, index) =>
        index === existingIndex
          ? {
              ...account,
              ...normalizedIncoming,
              lastAnalysis: incoming.lastAnalysis ?? account.lastAnalysis,
              startDate: incoming.startDate ?? account.startDate,
              endDate: incoming.endDate ?? account.endDate,
              documents: uniqueDocuments([
                ...account.documents,
                ...normalizedIncoming.documents,
              ]),
            }
          : account
      );
    }
  });

  const untouchedPending = state.pendingIdentification.filter(
    (doc) =>
      !resolvedIds.has(doc.id) && !failedIds.has(doc.id)
  );
  return {
    ...state,
    submittedInSession: true,
    accounts,
    pendingIdentification: uniqueDocuments([
      ...untouchedPending,
      ...(action.payload.failed || []).map((doc) =>
        preserveUiDocumentFields(state, doc)
      ),
    ]),
  };
};

export const reducer = (
  state: BankStatementsState,
  action: Action
): BankStatementsState => {
  switch (action.type) {
    case "LOAD_START":
      return { ...state, loading: true, loadError: null };
    case "LOAD_SUCCESS": {
      const accounts = normalizeAccounts(action.payload.accounts || [], state);
      // The server has no account association for a freshly uploaded statement,
      // so it reports it under pendingIdentification while we keep it on the
      // card it was uploaded into. Without this it would render in both places.
      const accountDocIds = new Set(
        accounts.flatMap((account) => account.documents.map((doc) => doc.id))
      );
      return {
        ...state,
        ...action.payload,
        accounts,
        pendingIdentification: uniqueDocuments(
          action.payload.pendingIdentification || []
        )
          .filter((doc) => !accountDocIds.has(doc.id))
          .map((doc) => preserveUiDocumentFields(state, doc)),
        loading: false,
        loaded: true,
        loadError: null,
      };
    }
    case "LOAD_ERROR":
      return {
        ...state,
        loading: false,
        loaded: true,
        loadError: action.payload,
      };
    case "SET_SCOPE_BUSY": {
      const scopes = new Set(state.busyScopes);
      action.busy ? scopes.add(action.scope) : scopes.delete(action.scope);
      return { ...state, busyScopes: Array.from(scopes) };
    }
    case "STAGE_DOCUMENT": {
      if (action.accountId != null) {
        return {
          ...state,
          pendingIdentification: state.pendingIdentification.filter(
            (doc) => doc.id !== action.doc.id
          ),
          accounts: state.accounts.map((account) =>
            account.id === action.accountId
              ? {
                  ...account,
                  documents: uniqueDocuments([
                    ...account.documents,
                    action.doc,
                  ]),
                }
              : account
          ),
        };
      }
      if (
        state.accounts.some((account) =>
          account.documents.some(
            (doc) => doc.id === action.doc.id
          )
        )
      ) {
        return state;
      }
      return {
        ...state,
        pendingIdentification: uniqueDocuments([
          ...state.pendingIdentification,
          action.doc,
        ]),
      };
    }
    case "UPDATE_DOCUMENT": {
      const updatedIds = new Set([action.doc.id]);
      return {
        ...state,
        pendingIdentification: updateDocumentsById(
          state.pendingIdentification,
          updatedIds,
          () => action.doc
        ),
        accounts: state.accounts.map((account) => ({
          ...account,
          documents: updateDocumentsById(
            account.documents,
            updatedIds,
            () => action.doc
          ),
        })),
      };
    }
    case "REMOVE_DOCUMENTS": {
      const docIds = new Set(action.docIds);
      return {
        ...state,
        pendingIdentification: state.pendingIdentification.filter(
          (doc) => !docIds.has(doc.id)
        ),
        accounts: state.accounts.map((account) => ({
          ...account,
          documents: account.documents.filter(
            (doc) => !docIds.has(doc.id)
          ),
        })),
      };
    }
    case "DELETE_ACCOUNT": {
      const deletedDocIds = new Set(action.deletedDocIds);
      return {
        ...state,
        accounts: state.accounts.filter(
          (account) => account.id !== action.accountId
        ),
        pendingIdentification: state.pendingIdentification.filter(
          (doc) => !deletedDocIds.has(doc.id)
        ),
      };
    }
    case "ANALYSIS_SUBMIT_START": {
      const submittingIds = new Set(action.docIds);
      const markSubmitting = (doc: PulseDocSummary) => ({
        ...doc,
        status: SUBMITTING_STATUS,
        ocrProcessingStatus: OCR_PENDING_STATUS,
      });
      return {
        ...state,
        submissionError: null,
        pendingIdentification: updateDocumentsById(
          state.pendingIdentification,
          submittingIds,
          markSubmitting
        ),
        accounts: state.accounts.map((account) => ({
          ...account,
          documents: updateDocumentsById(
            account.documents,
            submittingIds,
            markSubmitting
          ),
        })),
      };
    }
    case "ANALYSIS_SUBMITTED": {
      const submittedIds = new Set(action.docIds);
      const markSubmitted = (doc: PulseDocSummary) => ({
        ...doc,
        status: SUBMITTED_STATUS,
        ocrProcessingStatus: OCR_PENDING_STATUS,
      });
      return {
        ...state,
        submittedInSession: true,
        submissionError: null,
        pendingIdentification: state.pendingIdentification.map((doc) =>
          submittedIds.has(doc.id) ? markSubmitted(doc) : doc
        ),
        accounts: state.accounts.map((account) => {
          const containsSubmittedDoc = account.documents.some((doc) =>
            submittedIds.has(doc.id)
          );
          return containsSubmittedDoc
            ? {
                ...account,
                documents: updateDocumentsById(
                  account.documents,
                  submittedIds,
                  markSubmitted
                ),
              }
            : account;
        }),
      };
    }
    case "ANALYSIS_SUBMIT_ERROR": {
      const failedSubmissionIds = new Set(action.docIds);
      const restoreNotSubmitted = (doc: PulseDocSummary) => ({
        ...doc,
        status: NOT_SUBMITTED_STATUS,
        ocrProcessingStatus: null,
      });
      return {
        ...state,
        submissionError: action.message,
        pendingIdentification: updateDocumentsById(
          state.pendingIdentification,
          failedSubmissionIds,
          restoreNotSubmitted
        ),
        accounts: state.accounts.map((account) => ({
          ...account,
          documents: updateDocumentsById(
            account.documents,
            failedSubmissionIds,
            restoreNotSubmitted
          ),
        })),
      };
    }
    case "IDENTIFICATION_STARTED": {
      const docIds = new Set(action.docIds);
      const markIdentifying = (doc: PulseDocSummary) => ({
        ...doc,
        status: SUBMITTED_STATUS,
        ocrProcessingStatus: OCR_PENDING_STATUS,
      });
      return {
        ...state,
        pendingIdentification: updateDocumentsById(
          state.pendingIdentification,
          docIds,
          markIdentifying
        ),
        accounts: state.accounts.map((account) => ({
          ...account,
          documents: updateDocumentsById(
            account.documents,
            docIds,
            markIdentifying
          ),
        })),
      };
    }
    case "IDENTIFICATION_RESULT":
      return applyIdentificationResult(state, action);
    case "OCR_COMPLETE": {
      const completedIds = new Set(action.payload.docIds || []);
      const markCompleted = (doc: PulseDocSummary) =>
        completedIds.has(doc.id)
          ? { ...doc, ocrProcessingStatus: OCR_COMPLETED_STATUS }
          : doc;
      const accountsWithCompletedDocuments = state.accounts.map((account) => ({
        ...account,
        documents: account.documents.map(markCompleted),
      }));
      return {
        ...state,
        pendingIdentification:
          state.pendingIdentification.map(markCompleted),
        accounts: updateAccountByPayload(
          accountsWithCompletedDocuments,
          action.payload,
          (account) => ({ ...account, pipelineStatus: "processing" })
        ),
      };
    }
    case "ACCOUNT_PROCESSING":
      return {
        ...state,
        accounts: updateAccountByPayload(state.accounts, action.payload, (account) => ({
          ...account,
          pipelineStatus: action.status,
          lastAnalysis:
            action.status === "failed"
              ? {
                  ...account.lastAnalysis,
                  status: ANALYSIS_FAILURE_STATUS,
                  errorDetails: {
                    ...account.lastAnalysis?.errorDetails,
                    errors: [STATEMENT_PROCESSING_FAILED_ERROR],
                  },
                }
              : account.lastAnalysis,
        })),
      };
    case "RECONCILIATION_COMPLETE":
      return {
        ...state,
        accounts: updateAccountByPayload(state.accounts, action.payload, (account) => ({
          ...account,
          startDate: action.payload.startDate,
          endDate: action.payload.endDate,
          pipelineStatus: "complete",
          lastAnalysis: {
            status: ANALYSIS_SUCCESS_STATUS,
            errorDetails: { errors: null, errorResults: null },
            updatedAt: action.updatedAt,
          },
        })),
      };
    case "RECONCILIATION_FAILED":
      return {
        ...state,
        accounts: updateAccountByPayload(state.accounts, action.payload, (account) => ({
          ...account,
          pipelineStatus: "failed",
          lastAnalysis: {
            ...account.lastAnalysis,
            status: action.payload.status || ANALYSIS_FAILURE_STATUS,
            errorDetails: {
              ...account.lastAnalysis?.errorDetails,
              errors: action.payload.errors?.length
                ? action.payload.errors
                : [STATEMENT_PROCESSING_FAILED_ERROR],
              errorResults: account.lastAnalysis?.errorDetails?.errorResults ?? null,
            },
            updatedAt: action.updatedAt,
          },
        })),
      };
    case "DEADLINE_SUBMITTED":
      return {
        ...state,
        accounts: state.accounts.map((account) => {
          const matchesAccount =
            (action.payload.pulseBankAccountId != null &&
              account.id === action.payload.pulseBankAccountId) ||
            (account.scheduleId != null &&
              account.scheduleId === action.payload.scheduleId);
          return matchesAccount
            ? {
                ...account,
                isActionRequired: false,
                scheduleId: null,
                dueBy: null,
                dueDate: null,
                requiredStatementPeriod: null,
              }
            : account;
        }),
      };
    case "TRANSACTION_CONSENT_UPDATED":
      return {
        ...state,
        accounts: state.accounts.map((account) => {
          if (account.id !== action.payload.pulseBankAccountId) return account;
          return {
            ...account,
            pipelineStatus: pipelineStatusFromAnalysisStatus(
              action.payload.status,
              account.pipelineStatus || "idle"
            ),
            lastAnalysis: {
              ...account.lastAnalysis,
              status: action.payload.status,
              errorDetails: {
                ...account.lastAnalysis?.errorDetails,
                errors: action.payload.errors,
                noTransactionConsent: action.payload.noTransactionConsent,
              },
            },
          };
        }),
      };
    default:
      return state;
  }
};
